import type { FC, ReactNode } from 'react'
import type { Patient } from '@/lib/clinic/types'

import { useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { UserPlus } from 'lucide-react'
import { useClinic } from '@/hooks/use-clinic'
import { getStats, searchPatients } from '@/lib/clinic/selectors'
import { digitsOnly, formatPhone } from '@/lib/clinic/format'
import { formatDate } from '@/lib/clinic/dates'
import { PatientAvatar } from '@/components/app/patient-avatar'

import {
    Command,
    CommandDialog,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList
} from '@/components/ui/command'

export type SearchPurpose = 'profile' | 'visit' | 'prescription' | 'follow-up'

const PURPOSE_COPY: Record<SearchPurpose, { title: string, hint: string }> = {
    'profile': { title: "Find a patient", hint: "Search by name, phone number or patient ID to open the profile." },
    'visit': { title: "Start a visit", hint: "Choose the patient you are seeing now." },
    'prescription': { title: "Create a prescription", hint: "Choose the patient for this prescription." },
    'follow-up': { title: "Add a follow-up", hint: "Choose the patient who needs to come back." }
}

interface PatientSearchDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    purpose: SearchPurpose
    onSelect: (patient: Patient, purpose: SearchPurpose) => void
}

export const PatientSearchDialog: FC<PatientSearchDialogProps> = ({ open, onOpenChange, purpose, onSelect }): ReactNode => {
    const router = useRouter()
    const { data, index } = useClinic()
    const [query, setQuery] = useState('')
    const copy = PURPOSE_COPY[purpose]
    const trimmed = query.trim()

    const results = useMemo(() => {
        if (trimmed) return searchPatients(data.patients, trimmed).slice(0, 8)

        return [...data.patients]
            .sort((a, b) => {
                const lastA = getStats(index, a.id).lastVisit ?? ''
                const lastB = getStats(index, b.id).lastVisit ?? ''

                return lastB.localeCompare(lastA) || b.createdAt.localeCompare(a.createdAt)
            })
            .slice(0, 6)
    }, [trimmed, data.patients, index])

    const registerNew = (): void => {
        const params = new URLSearchParams()

        if (trimmed) {
            const looksLikePhone = /^[\d\s+()-]+$/.test(trimmed) && digitsOnly(trimmed).length >= 6

            params.set(looksLikePhone ? 'phone' : 'name', trimmed)
        }

        const search = params.toString()

        onOpenChange(false)
        void router.push(search ? `/patients/new?${search}` : '/patients/new')
    }

    return (
        <CommandDialog
            open={open}
            onOpenChange={onOpenChange}
            title={copy.title}
            description={copy.hint}
            className='sm:max-w-xl'
        >
            <Command shouldFilter={false} loop>
                <div className='px-3 pt-3 pb-2'>
                    <p className='font-heading text-sm font-semibold text-foreground'>
                        {copy.title}
                    </p>

                    <p className='text-xs text-muted-foreground'>
                        {copy.hint}
                    </p>
                </div>

                <CommandInput
                    value={query}
                    onValueChange={setQuery}
                    placeholder='Name, phone number or patient ID'
                    aria-label='Search patients'
                />

                <CommandList className='max-h-[min(60vh,26rem)] px-1 pb-1'>
                    {results.length > 0 && (
                        <CommandGroup heading={trimmed ? 'Matching patients' : 'Recently visited'}>
                            {results.map(patient => {
                                const stats = getStats(index, patient.id)

                                return (
                                    <CommandItem
                                        key={patient.id}
                                        value={patient.id}
                                        onSelect={() => {
                                            onOpenChange(false)
                                            onSelect(patient, purpose)
                                        }}
                                        className='gap-3 py-2'
                                    >
                                        <PatientAvatar name={patient.name} seed={patient.id} size='sm' />

                                        <div className='min-w-0 flex-1'>
                                            <p className='truncate font-medium text-foreground'>
                                                {patient.name}
                                            </p>

                                            <p className='truncate text-xs text-muted-foreground'>
                                                {patient.id} · {formatPhone(patient.phone)}
                                                {patient.place ? ` · ${patient.place}` : ''}
                                            </p>
                                        </div>

                                        <span className='hidden shrink-0 text-xs text-muted-foreground sm:block'>
                                            {stats.lastVisit ? `Last visit ${formatDate(stats.lastVisit)}` : 'No visits yet'}
                                        </span>
                                    </CommandItem>
                                )
                            })}
                        </CommandGroup>
                    )}

                    {trimmed && results.length === 0 && (
                        <p className='px-3 py-6 text-center text-sm text-muted-foreground'>
                            No patient matches &ldquo;{trimmed}&rdquo;.
                        </p>
                    )}

                    <CommandGroup heading={trimmed && results.length === 0 ? 'New patient' : 'Not in the list?'}>
                        <CommandItem value='register-new-patient' onSelect={registerNew} className='gap-3 py-2'>
                            <span className='flex size-8 items-center justify-center rounded-full bg-accent text-accent-foreground'>
                                <UserPlus className='size-4' />
                            </span>

                            <span className='min-w-0 flex-1 truncate font-medium'>
                                Register a new patient
                                {trimmed ? <span className='font-normal text-muted-foreground'> &ldquo;{trimmed}&rdquo;</span> : null}
                            </span>
                        </CommandItem>
                    </CommandGroup>
                </CommandList>
            </Command>
        </CommandDialog>
    )
}
